import React, { useState, useRef, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { GripVertical } from 'lucide-react';

interface Column<T> {
  id: string;
  label: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  minWidth?: number;
  width?: number;
  resizable?: boolean;
  sortable?: boolean;
}

interface EnhancedTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowSelect?: (selectedRows: T[]) => void;
  selectable?: boolean;
  stickyHeader?: boolean;
  maxHeight?: string;
  onRowClick?: (row: T) => void;
  rowKey: keyof T;
}

export function EnhancedTable<T>({
  data,
  columns,
  onRowSelect,
  selectable = false,
  stickyHeader = true,
  maxHeight = 'calc(100vh - 400px)',
  onRowClick,
  rowKey,
}: EnhancedTableProps<T>) {
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [isResizing, setIsResizing] = useState(false);
  const tableRef = useRef<HTMLTableElement>(null);

  // Initialize column widths
  useEffect(() => {
    const initialWidths: Record<string, number> = {};
    columns.forEach((col) => {
      if (col.width) {
        initialWidths[col.id] = col.width;
      }
    });
    setColumnWidths(initialWidths);
  }, [columns]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allKeys = data.map((row) => String(row[rowKey]));
      setSelectedRows(new Set(allKeys));
      onRowSelect?.(data);
    } else {
      setSelectedRows(new Set());
      onRowSelect?.([]);
    }
  };

  const handleSelectRow = (row: T, checked: boolean) => {
    const key = String(row[rowKey]);
    const newSelected = new Set(selectedRows);
    
    if (checked) {
      newSelected.add(key);
    } else {
      newSelected.delete(key);
    }
    
    setSelectedRows(newSelected);
    const selectedData = data.filter((r) => newSelected.has(String(r[rowKey])));
    onRowSelect?.(selectedData);
  };

  const isRowSelected = (row: T) => {
    return selectedRows.has(String(row[rowKey]));
  };

  const handleMouseDown = (e: React.MouseEvent, columnId: string) => {
    e.preventDefault();
    setIsResizing(true);
    
    const startX = e.pageX;
    const startWidth = columnWidths[columnId] || 150;

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.pageX - startX;
      const newWidth = Math.max(startWidth + diff, 80);
      setColumnWidths((prev) => ({
        ...prev,
        [columnId]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const getCellValue = (row: T, column: Column<T>): React.ReactNode => {
    if (typeof column.accessor === 'function') {
      return column.accessor(row);
    }
    const value = row[column.accessor];
    return value as React.ReactNode;
  };

  const allSelected = data.length > 0 && selectedRows.size === data.length;
  const someSelected = selectedRows.size > 0 && selectedRows.size < data.length;

  return (
    <div className={cn("overflow-auto", !stickyHeader && "max-h-full")} style={{ maxHeight }}>
      <Table ref={tableRef}>
        <TableHeader className={cn(stickyHeader && "sticky top-0 bg-card z-10 shadow-sm")}>
          <TableRow>
            {selectable && (
              <TableHead className="w-12">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                  className={someSelected ? "data-[state=checked]:bg-primary" : ""}
                />
              </TableHead>
            )}
            {columns.map((column) => (
              <TableHead
                key={column.id}
                style={{
                  width: columnWidths[column.id] || column.width,
                  minWidth: column.minWidth || 80,
                  position: 'relative',
                }}
                className="group"
              >
                <div className="flex items-center justify-between">
                  <span>{column.label}</span>
                  {column.resizable !== false && (
                    <div
                      className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      onMouseDown={(e) => handleMouseDown(e, column.id)}
                    >
                      <GripVertical className="w-3 h-3 text-muted-foreground" />
                    </div>
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row, rowIndex) => {
            const isSelected = isRowSelected(row);
            return (
              <TableRow
                key={String(row[rowKey])}
                className={cn(
                  "cursor-pointer transition-colors",
                  isSelected && "bg-muted/50",
                  "hover:bg-muted/70"
                )}
                onClick={() => onRowClick?.(row)}
              >
                {selectable && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) => handleSelectRow(row, checked as boolean)}
                      aria-label={`Select row ${rowIndex + 1}`}
                    />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    style={{
                      width: columnWidths[column.id] || column.width,
                      minWidth: column.minWidth || 80,
                    }}
                  >
                    {getCellValue(row, column)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
